import React, { useEffect, useRef, useState, useMemo } from 'react';
import styles from './Style.module.css';

import TherapistIcon from '../../Assets/Images/no-preference.png';
import { useGetStaff } from '../../APICalling/useApiQueries';
import { ArrowLeft, Search } from 'lucide-react';
import TherapistCard from './TherapistCard/TherapistCard';
import NoPreference from './TherapistCard/NoPreference';
import { useLocation } from '../../Context/Location/LocationContext';
import { useUI } from '../../Context/UI/UIContext';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { resolveBookingDateTime } from '../../Utils/resolveBookingDateTime';

const timeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const rangesOverlap = (start1, end1, start2, end2) =>
  start1 < end2 && end1 > start2;

/**
 * Per-member services run sequentially from the booking start time.
 * Returns absolute start/end minutes for the service at `serviceIndex`.
 */
const getServiceWindow = (services, serviceIndex, bookingStartTime) => {
  const baseMinutes = timeToMinutes(bookingStartTime || "00:00:00") ?? 0;
  const durationBefore = (services || [])
    .slice(0, serviceIndex)
    .reduce((acc, svc) => acc + (parseInt(svc?.duration, 10) || 0), 0);
  const duration = parseInt(services?.[serviceIndex]?.duration, 10) || 0;
  const startMinutes = baseMinutes + durationBefore;
  return { startMinutes, endMinutes: startMinutes + duration };
};

const getTherapistId = (therapistLike) => {
  const raw = therapistLike?.id ?? therapistLike?.therapist_id ?? null;
  if (raw == null || raw === "") return null;
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? null : numeric;
};


export const TherapistList = ({
  active,
  handleClick,
  serviceId,
  serviceDuration,
  onSelectTherapist = () => {},
  memberForTherapist = null,
  serviceForTherapist = null,
  serviceIndexForTherapist = null,
}) => {
  const { selectedOutletID, outletTypeID } = useLocation();
  const { setIsCartActive } = useUI();
  const { members } = useGuests();
  const { selectedTime } = useSchedule();

    const [searchVal, setSearchVal] = useState("");
    const searchInputRef = useRef(null);

    const staffParams = useMemo(() => ({
      role: 2,
      availability: 0,
      outlet: selectedOutletID || null,
      outlet_type: outletTypeID,
      services: serviceId,
      duration: serviceDuration,
      pagination: 0,
      status: 1,
      leave: 0,
    }), [outletTypeID, selectedOutletID, serviceDuration, serviceId]);

    const { data: staffData, isLoading } = useGetStaff(staffParams, {
      enabled: active && !!serviceId,
    });

    const therapistList = staffData?.data?.data?.list?.staffs || [];

    /**
     * Therapists already assigned to a *different* member for an overlapping
     * time window are unavailable. Same-member assignments are ignored so the
     * same therapist can continue serving sequential services for one guest.
     */
    const busyTherapistIds = useMemo(() => {
      const busyIds = new Set();
      if (!serviceForTherapist || memberForTherapist == null) return busyIds;

      const currentMember = (members || []).find(
        (member) => member.id === memberForTherapist
      );
      if (!currentMember) return busyIds;

      const currentServices = currentMember.services || [];
      const currentServiceIndex =
        serviceIndexForTherapist != null
          ? serviceIndexForTherapist
          : currentServices.findIndex(
              (svc) =>
                svc.id === serviceForTherapist.id &&
                svc.count === serviceForTherapist.count
            );
      if (currentServiceIndex === -1) return busyIds;

      const bookingStartTime = resolveBookingDateTime(null, selectedTime).time || "00:00:00";
      const currentWindow = getServiceWindow(
        currentServices,
        currentServiceIndex,
        bookingStartTime
      );

      (members || []).forEach((member) => {
        if (member.id === memberForTherapist) return;

        (member.services || []).forEach((svc, index) => {
          const therapistId = getTherapistId(svc?.therapistData);
          if (therapistId == null) return;

          const otherWindow = getServiceWindow(
            member.services,
            index,
            bookingStartTime
          );
          if (
            rangesOverlap(
              currentWindow.startMinutes,
              currentWindow.endMinutes,
              otherWindow.startMinutes,
              otherWindow.endMinutes
            )
          ) {
            busyIds.add(therapistId);
          }
        });
      });

      return busyIds;
    }, [members, memberForTherapist, serviceForTherapist, selectedTime]);

  useEffect(() => {
    if (!active) return;
    if (!active || isLoading) return;
    const focusSearchInput = () => {
      searchInputRef.current?.focus({ preventScroll: true });
      searchInputRef.current?.select();
    };

    // Try once on open, then again after slide-in transition.
    focusSearchInput();
    const animationFrameId = window.requestAnimationFrame(focusSearchInput);
    const timeoutId = window.setTimeout(focusSearchInput, 180);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [active]);


  const handleSearch = (e) => {
    const value = e.target.value.trim();
    setSearchVal(value);
  };

const closeTherapistList = () => {
    handleClick();
};

const selectTherapist = (therapistData) => {
    const therapistId = getTherapistId(therapistData);
    if (therapistId != null && busyTherapistIds.has(therapistId)) return;
    onSelectTherapist(therapistData);
    setIsCartActive(true);
    handleClick();
};

const selectNoPreference = () => {
    if (isLoading || therapistList.length === 0) return;
    onSelectTherapist(null);
    setIsCartActive(true);
    handleClick();
};

  const showNoPreference =
    therapistList.length > 0 && (!searchVal || "no preference".includes(searchVal.toLowerCase()));

  const filteredTherapists = (therapistList || []).filter(
    (t) =>
      !searchVal ||
      t.alias?.toLowerCase().includes(searchVal.toLowerCase())
  );

  const selectedTherapistId = getTherapistId(serviceForTherapist?.therapistData);
  const isNoPreferenceSelected = selectedTherapistId == null;

  return (
    <div className={`${styles.therapistList} ${active ? styles.show : ""} wrapper cart-space-b`}>
        <div className={`${styles.searchHeader} space-x`}>
            <button className={`${styles.closeList} btn`} onClick={closeTherapistList} > <ArrowLeft size={20} color="var(--Neutral900)" /></button>
            <label className={styles.formControl}>
                <span className={styles.searchIcon}> <Search size={20} color="var(--Neutral900)" /></span>
                <input ref={searchInputRef} type='text' value={searchVal} onChange={handleSearch} placeholder='Name' className="form-control"/>
            </label>
        </div>
        <div className={styles.list}>
            {isLoading ? (
                <div className={styles.loaderWrapper}>
                    <span>Loading therapists...</span>
                </div>
            ) : (showNoPreference || filteredTherapists.length > 0) ? (
                <>
                {showNoPreference ? (
                    <NoPreference 
                    selectNoPreference={selectNoPreference}
                    isSelected={isNoPreferenceSelected}
                    />
                ) : null}
                {filteredTherapists.map((therapist) => {
                    const therapistId = getTherapistId(therapist);
                    const isBusy = busyTherapistIds.has(therapistId);
                    const isSelected =
                      selectedTherapistId != null &&
                      selectedTherapistId === therapistId;
                    return (
                      <TherapistCard 
                      key={therapist?.id} 
                      therapist={therapist} 
                      isBusy={isBusy} 
                      isSelected={isSelected}
                      selectTherapist={selectTherapist}
                    />
                    );
                })}
                </>
            ) : (
                <div className={`${styles.notFound} space-x`}>
                    <div>
                        <img src={TherapistIcon} alt='' />
                        <h6>Therapist not found</h6>
                    </div>
                </div>
            )}
        </div>

    </div>
  )
}
